import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SparbaumTree from "../components/SparbaumTree";
import { useCurrentUser } from "../lib/auth";
import { useGoalTree } from "./data";
import type { WidgetComponentProps } from "./registry";

export default function SparbaumWidget(_props: WidgetComponentProps) {
  const tree = useGoalTree();
  const user = useCurrentUser();
  const umbrella = tree?.[0];
  // Collapsed-state survives reloads — keyed per user so two accounts on the
  // same device can have different defaults. Default expanded (open) because
  // the tree is the widget's main feature. Hydrate explicitly once user.id is
  // known; reading localStorage in useState's initializer while `user` is
  // still null would clobber the saved value.
  const [open, setOpen] = useState<boolean>(true);
  const [hydrated, setHydrated] = useState<boolean>(false);
  useEffect(() => {
    if (!user) return;
    const key = `home:sparbaum:open:${user.id}`;
    try {
      const saved = window.localStorage.getItem(key);
      if (saved !== null) setOpen(saved === "1");
    } catch {
      // localStorage disabled — keep default.
    }
    setHydrated(true);
  }, [user]);
  useEffect(() => {
    if (!user || !hydrated) return;
    try {
      window.localStorage.setItem(`home:sparbaum:open:${user.id}`, open ? "1" : "0");
    } catch {
      // ignore
    }
  }, [user, open, hydrated]);

  if (!umbrella) return null;
  return (
    <section className="card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-left -ml-1 px-1 py-0.5 rounded hover:bg-ink-800/50 transition"
          aria-expanded={open}
          aria-label={open ? "Sparbaum einklappen" : "Sparbaum ausklappen"}
          title={open ? "Sparbaum einklappen" : "Sparbaum ausklappen"}
        >
          <span
            className={
              "text-ink-400 text-xs leading-none transition-transform " +
              (open ? "rotate-90" : "")
            }
            aria-hidden="true"
          >
            ▶
          </span>
          <h2 className="section-title">Sparbaum</h2>
        </button>
        <Link to="/goals" className="text-xs text-brand-300 hover:underline">
          Detail →
        </Link>
      </div>
      {open && (
        <div className="overflow-x-auto pb-2 mt-3">
          <SparbaumTree root={umbrella} expanded />
        </div>
      )}
    </section>
  );
}
